import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { userManager } from "../auth-config";
import {
	AI_CODE_REVIEWER_USERNAME,
	GITLAB_BASE_URL,
	GITLAB_GRAPHQL_URL,
	REFRESH_INTERVAL,
} from "../constants";
import {
	ENRICHMENT_BATCH_SIZE,
	ENRICHMENT_CONCURRENCY,
	createEnrichmentQuery,
} from "./createEnrichmentQuery";
import { mergePipelines } from "./mergePipelines";
import { projectRules } from "./projectRules";
import { useCachedStorage } from "./useCachedStorage";

const REQUEST_TIMEOUT_MS = 30000;
const MR_FIELDS_LIGHTWEIGHT = `
  id
  iid
  webPath
  title
  createdAt
  updatedAt
  state
  draft
  reference
  sourceBranch
  conflicts
  userNotesCount
  author {
    avatarUrl
    name
    username
    bot
  }
  project {
    archived
    fullPath
  }
`;

const REVIEW_REQUESTS_QUERY = `
  query getReviewRequests {
    currentUser {
      reviewRequests: reviewRequestedMergeRequests(state: opened, sort: UPDATED_DESC) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
    }
  }
`;

const MY_OPEN_MRS_QUERY = `
  query getMyOpenMRs {
    currentUser {
      openRequests: authoredMergeRequests(state: opened, sort: UPDATED_DESC) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
    }
  }
`;

// Separate user profile query that doesn't need frequent updates
const USER_PROFILE_QUERY = `
  query getUserProfile {
    currentUser {
      avatarUrl
      username
    }
  }
`;

const CLOSED_DATA_QUERY = `
  query getClosedData {
    currentUser {
      mergedRequests: authoredMergeRequests(state: merged, sort: MERGED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
      closedRequests: authoredMergeRequests(state: closed, sort: CLOSED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
      mergedReviewRequests: reviewRequestedMergeRequests(state: merged, sort: MERGED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
      closedReviewRequests: reviewRequestedMergeRequests(state: closed, sort: CLOSED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
    }
  }
`;

const excludeArchivedProjects = (mr) => !mr.project.archived;
const excludeMyPrs = (mr) => !mr.author.isMe;
const isAiCodeReviewer = (user) => user?.username === AI_CODE_REVIEWER_USERNAME;

const inFlightRequests = new Map();
const PROJECT_ENRICHMENT_CONCURRENCY = 4;
const WATCHED_MR_BATCH_SIZE = 20;
const INITIAL_MR_DATA = {
	pendingReviewMRs: [],
	myOpenMRs: [],
	myMergedMRs: [],
	myClosedMRs: [],
	recentlyClosedMRs: [],
	watchedMRs: [],
	userAvatar: null,
	username: null,
	loaded: false,
	hasIncompleteResults: false,
	lastUpdated: null,
};

const hasCachedMergeRequestData = (data) =>
	Boolean(
		data?.loaded ||
			data?.pendingReviewMRs?.length ||
			data?.myOpenMRs?.length ||
			data?.myMergedMRs?.length ||
			data?.myClosedMRs?.length ||
			data?.recentlyClosedMRs?.length ||
			data?.watchedMRs?.length,
	);

const isGlobalMergeRequestId = (id) =>
	typeof id === "string" && id.startsWith("gid://gitlab/MergeRequest/");

const createWatchedMergeRequestsQuery = (ids) => {
	const mergeRequests = ids
		.map(
			(id, index) => `
				watchedMergeRequest${index}: mergeRequest(id: ${JSON.stringify(id)}) {
					${MR_FIELDS_LIGHTWEIGHT}
				}
			`,
		)
		.join("\n");

	return `
		query getWatchedMergeRequests {
			${mergeRequests}
		}
	`;
};

const dedupeRequest = async (key, requestFn) => {
	if (inFlightRequests.has(key)) {
		return inFlightRequests.get(key);
	}

	const promise = requestFn().finally(() => {
		inFlightRequests.delete(key);
	});

	inFlightRequests.set(key, promise);
	return promise;
};

const runWithConcurrency = async (tasks, concurrency) => {
	const results = new Array(tasks.length);
	let nextTask = 0;

	const workers = Array.from(
		{ length: Math.min(concurrency, tasks.length) },
		async () => {
			while (nextTask < tasks.length) {
				const taskIndex = nextTask++;
				results[taskIndex] = await tasks[taskIndex]();
			}
		},
	);

	await Promise.all(workers);
	return results;
};

// Module-level guard so concurrent hook instances don't each fire signinSilent
// or bounce the user through multiple redirects.
let silentRenewalPromise = null;
let redirectingToLogin = false;
const forceReauth = () => {
	if (redirectingToLogin) return;
	// Skip when we're already on an auth-related page — those flows handle
	// their own redirects and firing another kicks off a race.
	const pathname = window.location?.pathname || "";
	if (pathname === "/callback" || pathname === "/login") return;
	redirectingToLogin = true;
	console.warn("Silent renewal failed, forcing full re-auth");
	userManager.signinRedirect().catch((err) => {
		console.error("signinRedirect failed:", err);
		redirectingToLogin = false;
	});
};
const triggerSilentRenewal = () => {
	if (silentRenewalPromise) return silentRenewalPromise;
	silentRenewalPromise = userManager
		.signinSilent()
		.catch((err) => {
			console.error("Silent token renewal failed:", err);
			forceReauth();
			return null;
		})
		.finally(() => {
			silentRenewalPromise = null;
		});
	return silentRenewalPromise;
};

export const useMergeRequests = (watchProjects, watchedIds = []) => {
	const { user } = useAuth();
	const [isLoadingOpen, setIsLoadingOpen] = useState(false);
	const [isLoadingClosed, setIsLoadingClosed] = useState(false);
	const [hasQueries, setHasQueries] = useState(false);
	const [error, setError] = useState(null);
	const [lastFailedRequest, setLastFailedRequest] = useState(null);
	const [data, setData] = useCachedStorage("gitlab-mr-data", INITIAL_MR_DATA);
	const dataRef = useRef(data);
	const [profileFetched, setProfileFetched] = useState(false);
	// When a 401 is seen we pause polling until the token actually rotates.
	const authFailedTokenRef = useRef(null);

	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	const executeQuery = useCallback(
		async (query, variables = {}) => {
			setHasQueries(true);
			const key = JSON.stringify({ query, variables });

			return dedupeRequest(key, async () => {
				const controller = new AbortController();
				const timeoutId = setTimeout(
					() => controller.abort(),
					REQUEST_TIMEOUT_MS,
				);

				try {
					const response = await fetch(GITLAB_GRAPHQL_URL, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${user?.access_token}`,
						},
						body: JSON.stringify({
							query,
							variables,
						}),
						signal: controller.signal,
					});

					if (!response.ok) {
						if (response.status === 401) {
							authFailedTokenRef.current = user?.access_token || null;
							triggerSilentRenewal();
						}
						setLastFailedRequest({ query, variables });
						throw new Error(
							response.status === 401
								? "GitLab session expired, refreshing token"
								: "GraphQL request failed",
						);
					}

					const result = await response.json();
					if (result.errors) {
						if (result.data) {
							console.error("Incomplete GraphQL results:", result.errors);
							setError(
								"Gitlab returned partial data from some queries, some details may not be shown.",
							);
							return { data: result.data, incomplete: true };
						}
						setLastFailedRequest({ query, variables });
						throw new Error(result.errors[0].message);
					}

					setLastFailedRequest(null);
					return { data: result.data, incomplete: false };
				} catch (error) {
					if (error.name === "AbortError") {
						throw new Error("GitLab request timed out");
					}
					throw error;
				} finally {
					clearTimeout(timeoutId);
				}
			}).finally(() => {
				if (inFlightRequests.size === 0) {
					setHasQueries(false);
				}
			});
		},
		[user],
	);

	const avatarUrl = useCallback((url) => {
		if (!url) return null;
		return url.startsWith("https://") ? url : `${GITLAB_BASE_URL}${url}`;
	}, []);

	const transformMergeRequest = useCallback(
		(mr) => {
			if (!mr) {
				console.warn("Received null merge request in transformMergeRequest");
				return null;
			}

			const pipeline = mergePipelines(mr);
			const approvers = mr.approvedBy?.nodes || [];
			const reviewers = mr.reviewers?.nodes || [];
			const aiReviewMissing = !approvers.some(isAiCodeReviewer);
			const hasHumanReview =
				approvers.some((reviewer) => !isAiCodeReviewer(reviewer)) ||
				reviewers.some((reviewer) => {
					const reviewState = reviewer.mergeRequestInteraction?.reviewState;
					return (
						!isAiCodeReviewer(reviewer) &&
						reviewState &&
						!["UNREVIEWED", "REVIEW_STARTED"].includes(reviewState)
					);
				});

			return {
				id: mr.id,
				iid: mr.iid,
				webUrl: `${GITLAB_BASE_URL}${mr.webPath}`,
				title: mr.title,
				createdAt: mr.createdAt,
				updatedAt: mr.updatedAt,
				state: mr.state,
				draft: mr.draft,
				reference: mr.reference,
				sourceBranch: mr.sourceBranch,
				projectPath:
					mr.project?.fullPath ||
					mr.reference?.split("!")[0] ||
					mr.webPath?.split("/-/")[0]?.split("/").slice(-2).join("/") ||
					"",
				userNotesCount: mr.userNotesCount,
				conflicts: mr.conflicts,
				author: {
					name: mr.author.name,
					username: mr.author.username,
					avatarUrl: avatarUrl(mr.author.avatarUrl),
					bot: mr.author.bot || mr.author.username === "renovate",
					isMe: user?.profile?.username === mr.author.username,
				},
				diffStatsSummary: mr.diffStatsSummary || {
					additions: 0,
					changes: 0,
					deletions: 0,
				},
				discussions: Math.max(
					0,
					(mr.resolvableDiscussionsCount || 0) -
						(mr.resolvedDiscussionsCount || 0),
				),
				aiReviewMissing,
				hasHumanReview,
				headPipeline:
					(pipeline && {
						status: pipeline.active ? "running" : pipeline.status.toLowerCase(),
						latest: pipeline.latest,
						name: pipeline.name,
						totalJobs: pipeline.totalJobs?.count || 0,
						failedJobs: pipeline.failedJobs?.count || 0,
						passedJobs: pipeline.passedJobs?.count || 0,
						mergeRequestEventType: pipeline.mergeRequestEventType,
					}) ||
					null,
				approvalsLeft: mr.approvalsLeft,
				approvers: approvers.map((reviewer) => ({
					avatarUrl: avatarUrl(reviewer.avatarUrl),
					name: reviewer.name,
					username: reviewer.username,
				})),
				reviewers,
				/* UNREVIEWED, REVIEWED, REQUESTED_CHANGES, APPROVED, UNAPPROVED, REVIEW_STARTED */
				myReview:
					(mr.reviewers?.nodes || []).filter(
						(r) => r.username === user?.profile?.username,
					)?.[0]?.mergeRequestInteraction?.reviewState ||
					((mr.approvedBy?.nodes || []).find(
						(reviewer) => reviewer.username === user?.profile?.username,
					)
						? "APPROVED"
						: "UNREVIEWED"),
				project: mr.project,
				...(mr.projectFields && { projectFields: mr.projectFields }),
			};
		},
		[avatarUrl, user],
	);

	// Enrich MRs with expensive fields in batches to avoid timeouts
	const enrichMergeRequestsWithExpensiveFields = useCallback(
		async (mrs) => {
			if (!mrs || mrs.length === 0) return;

			const tasks = [];
			for (let i = 0; i < mrs.length; i += ENRICHMENT_BATCH_SIZE) {
				const batch = mrs.slice(i, i + ENRICHMENT_BATCH_SIZE);
				tasks.push(async () => {
					const query = createEnrichmentQuery(batch);

					try {
						const result = await executeQuery(query);
						for (const [index, originalMR] of batch.entries()) {
							const enrichedMR = result.data[`mergeRequest${index}`];
							if (enrichedMR) {
								Object.assign(originalMR, {
									diffStatsSummary: enrichedMR.diffStatsSummary,
									pipelines: enrichedMR.pipelines,
									headPipeline: enrichedMR.headPipeline,
									approvedBy: enrichedMR.approvedBy,
									reviewers: enrichedMR.reviewers,
									approvalsLeft: enrichedMR.approvalsLeft,
									resolvableDiscussionsCount:
										enrichedMR.resolvableDiscussionsCount,
									resolvedDiscussionsCount: enrichedMR.resolvedDiscussionsCount,
								});
							}
						}
					} catch (err) {
						const projects = [
							...new Set(batch.map((mr) => mr.project.fullPath)),
						].join(", ");
						console.error(`Error enriching MRs for projects ${projects}:`, err);
					}
				});
			}

			await runWithConcurrency(tasks, ENRICHMENT_CONCURRENCY);
		},
		[executeQuery],
	);

	// Fetch project-specific fields independently from the generic enrichment.
	const fetchProjectMergeRequestFields = useCallback(
		async (mrs) => {
			// Group MRs by project that need additional fields
			const projectMrs = {};

			for (const mr of mrs) {
				const projectPath = mr.project.fullPath;

				if (projectRules?.[projectPath]?.mergeRequestFields) {
					projectMrs[projectPath] ??= {};
					projectMrs[projectPath][mr.iid] = mr;
				}
			}

			const tasks = [];

			for (const projectFullPath in projectMrs) {
				const projectRule = projectRules[projectFullPath];
				const allIids = Object.keys(projectMrs[projectFullPath]);
				const fieldBatches = [
					{
						fields: projectRule.mergeRequestFields,
						iids: allIids,
					},
				];

				if (projectRule.batchedMergeRequestFields) {
					const { batchSize, fields } = projectRule.batchedMergeRequestFields;
					for (let i = 0; i < allIids.length; i += batchSize) {
						fieldBatches.push({
							fields,
							iids: allIids.slice(i, i + batchSize),
						});
					}
				}

				for (const { fields, iids } of fieldBatches) {
					tasks.push(async () => {
						const iidList = iids.map((iid) => JSON.stringify(iid)).join(",");
						const query = `
	          query getProjectMrFields {
	            project(fullPath: ${JSON.stringify(projectFullPath)}) {
	              mergeRequests(iids: [${iidList}]) {
	                nodes {
	                  iid
	                  ${fields}
	                }
	              }
	            }
	          }`;

						const result = await executeQuery(query);
						const nodes = result.data.project.mergeRequests.nodes;
						for (const node of nodes) {
							const mr = projectMrs[projectFullPath][node.iid];
							mr.projectFields = { ...mr.projectFields, ...node };
						}
					});
				}
			}

			await runWithConcurrency(tasks, PROJECT_ENRICHMENT_CONCURRENCY);
		},
		[executeQuery],
	);

	const applyProjectRules = useCallback((mrs) => {
		for (const mr of mrs) {
			const projectPath = mr.project.fullPath;
			mr.additionalRules = projectRules?.[projectPath]?.additionalRules?.(mr);
		}
	}, []);

	// Fetch user profile data - only needs to run once
	const fetchUserProfile = useCallback(async () => {
		if (profileFetched || !user?.access_token) return;

		return dedupeRequest("fetchUserProfile", async () => {
			try {
				const profileResult = await executeQuery(USER_PROFILE_QUERY);
				setData((prevData) => ({
					...prevData,
					userAvatar: avatarUrl(profileResult.data.currentUser.avatarUrl),
					username: profileResult.data.currentUser.username,
				}));
				setProfileFetched(true);
			} catch (err) {
				console.error("Error fetching user profile:", err);
			}
		});
	}, [executeQuery, avatarUrl, profileFetched, user, setData]);

	const fetchOpenData = useCallback(async () => {
		if (!user?.access_token) return;

		setIsLoadingOpen(true);
		let hasAnyIncompleteResults = false;

		try {
			// Fetch watched project MRs with lightweight fields
			const watchedProjectQueries = Object.values(watchProjects).map(
				(project) =>
					executeQuery(`
          query getWatchedProjectMRs {
            project (fullPath: "${project.id}") {
              mergeRequests (state: opened, sort: UPDATED_DESC, first: 50) {
                nodes {
                  ${MR_FIELDS_LIGHTWEIGHT}
                }
              }
            }
          }
        `),
			);

			// Execute queries in parallel
			const [
				reviewRequestsResponse,
				watchedProjectResponses,
				myOpenMRsResponse,
			] = await Promise.allSettled([
				executeQuery(REVIEW_REQUESTS_QUERY),
				Promise.allSettled(watchedProjectQueries),
				executeQuery(MY_OPEN_MRS_QUERY),
			]);

			// Track if any query returned incomplete results
			if (
				reviewRequestsResponse.status === "fulfilled" &&
				reviewRequestsResponse.value?.incomplete
			) {
				hasAnyIncompleteResults = true;
			}
			if (watchedProjectResponses.status === "fulfilled") {
				for (const projectResponse of watchedProjectResponses.value) {
					if (
						projectResponse.status === "fulfilled" &&
						projectResponse.value?.incomplete
					) {
						hasAnyIncompleteResults = true;
					}
				}
			}
			if (
				myOpenMRsResponse.status === "fulfilled" &&
				myOpenMRsResponse.value?.incomplete
			) {
				hasAnyIncompleteResults = true;
			}

			// Process review requests
			let reviewRequests = [];

			if (reviewRequestsResponse.status !== "fulfilled") {
				console.error(
					"Error fetching review requests:",
					reviewRequestsResponse.reason,
				);
				throw new Error("Could not retrieve all Merge Requests to review");
			}

			reviewRequests = (
				reviewRequestsResponse.value.data.currentUser.reviewRequests.nodes || []
			).filter((mr) => mr != null);

			// Process watched project MRs
			if (watchedProjectResponses.status !== "fulfilled") {
				console.error(
					"Error fetching any project review requests:",
					watchedProjectResponses.reason,
				);
				throw new Error(
					"Could not retrieve any Project Merge Requests to review",
				);
			}

			for (const projectResponse of watchedProjectResponses.value) {
				if (projectResponse.status !== "fulfilled") {
					console.error(
						"Error fetching project review requests:",
						projectResponse.reason,
					);
					throw new Error(
						"Could not retrieve some Project Merge Requests to review",
					);
				}

				const projectMRs = (
					projectResponse.value.data.project.mergeRequests.nodes || []
				).filter((mr) => mr != null);

				// Add MRs not already in reviewRequests
				for (const mr of projectMRs) {
					if (!reviewRequests.some((req) => req.iid === mr.iid)) {
						reviewRequests.push(mr);
					}
				}
			}

			await Promise.all([
				enrichMergeRequestsWithExpensiveFields(reviewRequests),
				fetchProjectMergeRequestFields(reviewRequests),
			]);

			// Process pending review MRs
			const pendingReviewMRs = reviewRequests
				.map(transformMergeRequest)
				.filter(excludeArchivedProjects)
				.filter(excludeMyPrs);

			applyProjectRules(pendingReviewMRs);

			setData((prevData) => ({
				...prevData,
				pendingReviewMRs,
				loaded: true,
			}));

			if (myOpenMRsResponse.status !== "fulfilled") {
				throw myOpenMRsResponse.reason;
			}
			const myOpenMRsNodes = (
				myOpenMRsResponse.value.data.currentUser.openRequests.nodes || []
			).filter((mr) => mr != null);

			await Promise.all([
				enrichMergeRequestsWithExpensiveFields(myOpenMRsNodes),
				fetchProjectMergeRequestFields(myOpenMRsNodes),
			]);

			const filteredMyOpenMRs = myOpenMRsNodes
				.map(transformMergeRequest)
				.filter(excludeArchivedProjects);

			applyProjectRules(filteredMyOpenMRs);

			setData((prevData) => ({
				...prevData,
				myOpenMRs: filteredMyOpenMRs,
				loaded: true,
			}));

			// Update hasIncompleteResults flag after all queries complete
			setData((prevData) => ({
				...prevData,
				hasIncompleteResults: hasAnyIncompleteResults,
				lastUpdated: new Date().toISOString(),
			}));
			setError("");
		} catch (err) {
			console.error("Error in fetchOpenData:", err);
			setError(
				hasCachedMergeRequestData(dataRef.current)
					? `Showing cached data. Last refresh failed: ${err.message}`
					: err.message,
			);
		} finally {
			setIsLoadingOpen(false);
		}
	}, [
		executeQuery,
		transformMergeRequest,
		user,
		setData,
		enrichMergeRequestsWithExpensiveFields,
		fetchProjectMergeRequestFields,
		applyProjectRules,
		watchProjects,
	]);

	const fetchClosedData = useCallback(async () => {
		if (!user?.access_token) return;

		setIsLoadingClosed(true);

		try {
			const closedDataResult = await executeQuery(CLOSED_DATA_QUERY);
			const closedData = closedDataResult.data.currentUser;

			const myMergedNodes = (closedData.mergedRequests.nodes || []).filter(
				(mr) => mr != null,
			);
			const myClosedNodes = (closedData.closedRequests.nodes || []).filter(
				(mr) => mr != null,
			);
			const reviewMergedNodes = (
				closedData.mergedReviewRequests.nodes || []
			).filter((mr) => mr != null);
			const reviewClosedNodes = (
				closedData.closedReviewRequests.nodes || []
			).filter((mr) => mr != null);

			const myMergedMRs = myMergedNodes
				.map(transformMergeRequest)
				.filter(excludeArchivedProjects)
				.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
			const myClosedMRs = myClosedNodes
				.map(transformMergeRequest)
				.filter(excludeArchivedProjects)
				.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

			const recentlyClosedMRs = [
				...new Map(
					[
						...myMergedNodes,
						...myClosedNodes,
						...reviewMergedNodes,
						...reviewClosedNodes,
					]
						.map(transformMergeRequest)
						.filter(excludeArchivedProjects)
						.map((mr) => [mr.iid, mr]),
				).values(),
			]
				.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
				.slice(0, 30);

			setData((prevData) => ({
				...prevData,
				recentlyClosedMRs,
				myMergedMRs,
				myClosedMRs,
			}));
		} catch (err) {
			console.error("Error fetching closed merge requests:", err);
		} finally {
			setIsLoadingClosed(false);
		}
	}, [executeQuery, transformMergeRequest, user, setData]);

	const fetchWatchedMergeRequests = useCallback(async () => {
		if (!user?.access_token) return;

		const mergeRequestIds = [...new Set(watchedIds)].filter(
			isGlobalMergeRequestId,
		);

		if (mergeRequestIds.length === 0) {
			setData((prevData) =>
				prevData.watchedMRs?.length
					? { ...prevData, watchedMRs: [] }
					: prevData,
			);
			return;
		}

		try {
			const tasks = [];

			for (let i = 0; i < mergeRequestIds.length; i += WATCHED_MR_BATCH_SIZE) {
				const batch = mergeRequestIds.slice(i, i + WATCHED_MR_BATCH_SIZE);
				tasks.push(async () => {
					const result = await executeQuery(
						createWatchedMergeRequestsQuery(batch),
					);

					return batch
						.map((_, index) => result.data[`watchedMergeRequest${index}`])
						.filter((mr) => mr != null);
				});
			}

			const watchedNodes = (
				await runWithConcurrency(tasks, PROJECT_ENRICHMENT_CONCURRENCY)
			).flat();

			await Promise.all([
				enrichMergeRequestsWithExpensiveFields(watchedNodes),
				fetchProjectMergeRequestFields(watchedNodes),
			]);

			const watchedMRs = watchedNodes
				.map(transformMergeRequest)
				.filter(excludeArchivedProjects);

			applyProjectRules(watchedMRs);

			setData((prevData) => ({
				...prevData,
				watchedMRs,
			}));
		} catch (err) {
			console.error("Error fetching watched merge requests:", err);
		}
	}, [
		executeQuery,
		transformMergeRequest,
		user,
		setData,
		enrichMergeRequestsWithExpensiveFields,
		fetchProjectMergeRequestFields,
		applyProjectRules,
		watchedIds,
	]);

	const fetchAllData = useCallback(async () => {
		await Promise.allSettled([
			fetchOpenData(),
			fetchClosedData(),
			fetchWatchedMergeRequests(),
		]);
	}, [fetchOpenData, fetchClosedData, fetchWatchedMergeRequests]);

	useEffect(() => {
		if (user?.access_token && !profileFetched) {
			fetchUserProfile();
		}
	}, [user, fetchUserProfile, profileFetched]);

	// Keep stable refs so the retry effect only fires when the token itself
	// changes, not when callbacks get re-created.
	const fetchOpenDataRef = useRef(fetchOpenData);
	const fetchClosedDataRef = useRef(fetchClosedData);
	const fetchWatchedMergeRequestsRef = useRef(fetchWatchedMergeRequests);
	const lastFailedRequestRef = useRef(lastFailedRequest);
	useEffect(() => {
		fetchOpenDataRef.current = fetchOpenData;
		fetchClosedDataRef.current = fetchClosedData;
		fetchWatchedMergeRequestsRef.current = fetchWatchedMergeRequests;
		lastFailedRequestRef.current = lastFailedRequest;
	});

	const accessToken = user?.access_token;
	useEffect(() => {
		if (!accessToken) return;
		// Only retry if we have a fresh token that differs from the one that failed.
		if (authFailedTokenRef.current === accessToken) return;
		authFailedTokenRef.current = null;

		const failed = lastFailedRequestRef.current;
		if (!failed) return;

		console.log("Retrying failed request with new token");
		(async () => {
			try {
				setError(null);
				if (failed.query.includes("getWatchedMergeRequests")) {
					await fetchWatchedMergeRequestsRef.current();
				} else if (
					failed.query === REVIEW_REQUESTS_QUERY ||
					failed.query === MY_OPEN_MRS_QUERY ||
					failed.query.includes("getWatchedProjectMRs") ||
					failed.query.includes("getProjectMrFields")
				) {
					await fetchOpenDataRef.current();
				} else if (
					failed.query.includes("Closed") ||
					failed.query.includes("Merged")
				) {
					await fetchClosedDataRef.current();
				}
			} catch (err) {
				console.error("Retry with new token failed:", err);
				setError(err.message);
			}
		})();
	}, [accessToken]);

	// Keep a stable ref to the latest fetchAllData so the polling loop
	// does not restart every time its dependencies change identity
	// (which happens on every token refresh, setData, etc).
	const fetchAllDataRef = useRef(fetchAllData);
	useEffect(() => {
		fetchAllDataRef.current = fetchAllData;
	}, [fetchAllData]);

	useEffect(() => {
		if (!accessToken) return;

		let running = true;
		let timeoutId = null;
		const run = () => {
			if (!running) return;
			// Pause polling while auth is broken; the retry effect will
			// resume things once a fresh token arrives.
			if (authFailedTokenRef.current === accessToken) {
				timeoutId = setTimeout(run, REFRESH_INTERVAL);
				return;
			}
			fetchAllDataRef.current().finally(() => {
				if (!running) return;
				timeoutId = setTimeout(run, REFRESH_INTERVAL);
			});
		};
		run();
		return () => {
			running = false;
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [accessToken]);

	const closeMergeRequest = useCallback(
		async (mr) => {
			if (!user?.access_token) {
				throw new Error("Not signed in");
			}
			if (!mr?.projectPath || !mr?.iid) {
				throw new Error("Missing merge request context");
			}
			if (user?.profile?.username !== mr?.author?.username) {
				throw new Error("Only the author can close this merge request");
			}

			const controller = new AbortController();
			const timeoutId = setTimeout(
				() => controller.abort(),
				REQUEST_TIMEOUT_MS,
			);
			try {
				const response = await fetch(
					`${GITLAB_BASE_URL}/api/v4/projects/${encodeURIComponent(
						mr.projectPath,
					)}/merge_requests/${mr.iid}?state_event=close`,
					{
						method: "PUT",
						headers: {
							Authorization: `Bearer ${user.access_token}`,
							Accept: "application/json",
						},
						signal: controller.signal,
					},
				);
				if (!response.ok) {
					const text = await response.text().catch(() => "");
					throw new Error(
						`Failed to close MR (${response.status})${text ? `: ${text}` : ""}`,
					);
				}
				return await response.json().catch(() => null);
			} catch (err) {
				if (err.name === "AbortError") {
					throw new Error("Close request timed out");
				}
				throw err;
			} finally {
				clearTimeout(timeoutId);
			}
		},
		[user],
	);

	return {
		...data,
		loading: isLoadingOpen && !data.loaded,
		loadingClosed: isLoadingClosed && !data.loaded,
		error,
		lastUpdated: data.lastUpdated,
		refetch: fetchAllData,
		isRefreshing: hasQueries,
		closeMergeRequest,
	};
};
